import {FC, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {Stack, Typography} from "@mui/material";
import {useAppDispatch, useAppSelector} from "../../../hooks/redux";
import {selectCurrentUser} from "../../users/model/selectors";
import {fetchCheckAuth, fetchOut} from "../../auth/model/actions";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import BaseModal from "../../../components/modals/BaseModal";
import MyButton from "../../../styles/theme/customizations/MyButton";
import ConsentFields from "./ConsentFields";
import {legalAPI} from "../api";

/**
 * Модалка повторного принятия согласий (паттерн click-wrap). Показывается,
 * когда needs_consent=true (старые юзеры без согласия / выкатка новой версии).
 * Закрытие только через «Принять» или «Выйти».
 *
 * Два важных правила click-wrap:
 *  1. Документы должны быть ЧИТАЕМЫ до согласия. Ссылки в ConsentFields
 *     открывают /legal/* в новой вкладке — поэтому на самих страницах /legal/*
 *     модалку ПРЯЧЕМ (иначе она перекрыла бы текст и в новой вкладке, т.к. та
 *     тоже авторизована через общий localStorage).
 *  2. Согласие — осознанное действие: чекбоксы пусты по умолчанию, кнопка
 *     «Принять» активна только когда отмечены ОБА (как на формах регистрации).
 */
const ConsentReacceptModal: FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(selectCurrentUser);
    const versions = currentUser?.legal_versions;

    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptPdConsent, setAcceptPdConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // На страницах документов модалку не показываем — иначе их нельзя прочитать.
    const onLegalPage = location.pathname.startsWith("/legal");
    const open =
        !!currentUser?.needs_consent &&
        !currentUser?.consent_withdrawn &&
        !onLegalPage;

    // Сброс чекбоксов при каждом открытии (новая версия — заново и осознанно).
    useEffect(() => {
        if (open) {
            setAcceptTerms(false);
            setAcceptPdConsent(false);
        }
    }, [open]);

    const canAccept =
        acceptTerms &&
        acceptPdConsent &&
        !!versions?.terms_of_use &&
        !!versions?.pd_consent;

    const handleAccept = async () => {
        if (!canAccept || !versions?.terms_of_use || !versions?.pd_consent) return;
        setSubmitting(true);
        try {
            await legalAPI.accept({
                accept_terms: true,
                accept_pd_consent: true,
                terms_version: versions.terms_of_use,
                pd_consent_version: versions.pd_consent,
            });
            // Перечитываем /auth/me — needs_consent станет false, модалка закроется.
            await dispatch(fetchCheckAuth());
        } catch {
            dispatch(setMessage({
                severity: MESSAGE_SEVERITY.error,
                text: "Не удалось сохранить согласие. Попробуйте ещё раз.",
            }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <BaseModal
            open={open}
            onClose={() => undefined}
            hideCloseButton
            title="Обновлённые условия"
            maxWidth="sm"
            actions={
                <>
                    <MyButton
                        variant="outlined"
                        color="neutral"
                        onClick={() => dispatch(fetchOut())}
                        disabled={submitting}
                    >
                        Выйти
                    </MyButton>
                    <MyButton
                        variant="contained"
                        onClick={handleAccept}
                        loading={submitting}
                        disabled={!canAccept || submitting}
                    >
                        Принять
                    </MyButton>
                </>
            }
        >
            <Stack spacing={2} sx={{pb: 1}}>
                <Typography variant="body2" color="text.secondary">
                    Чтобы продолжить пользоваться сервисом, ознакомьтесь с документами
                    (откроются в новой вкладке) и примите актуальные условия.
                </Typography>
                <ConsentFields
                    acceptTerms={acceptTerms}
                    acceptPdConsent={acceptPdConsent}
                    onChangeTerms={setAcceptTerms}
                    onChangePdConsent={setAcceptPdConsent}
                />
            </Stack>
        </BaseModal>
    );
};

export default ConsentReacceptModal;
